<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  propertyMatchForHouse,
  propertyMatchGeoRows,
  propertyMatchHasBreakdown,
  propertyMatchReasonLabels,
  propertyMatchScoreLabel,
  propertyMatchStatusLabel,
} from '../utils/visitorInfoPresenter';

const props = defineProps({
  match: { type: Object, default: null },
  house: { type: Object, default: null },
  compact: { type: Boolean, default: false },
});

const { t } = useI18n();

const persistedMatch = computed(
  () => props.match || propertyMatchForHouse(props.house) || null
);
const hasBreakdown = computed(() =>
  propertyMatchHasBreakdown(persistedMatch.value)
);
const scoreLabel = computed(() =>
  propertyMatchScoreLabel(persistedMatch.value)
);
const statusLabel = computed(() =>
  propertyMatchStatusLabel(persistedMatch.value)
);
const reasonLabels = computed(() =>
  propertyMatchReasonLabels(persistedMatch.value).slice(
    0,
    props.compact ? 2 : 4
  )
);
const geoRows = computed(() => propertyMatchGeoRows(persistedMatch.value));
</script>

<template>
  <div class="contents">
    <section
      v-if="hasBreakdown"
      class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3"
      data-testid="property-match-breakdown"
    >
      <div class="flex items-start justify-between gap-3">
        <div>
          <p class="text-lais-label-sm font-semibold text-lais-text-muted">
            {{ t('LEADS.VISITOR_INFO.MATCH_BREAKDOWN.TITLE') }}
          </p>
          <p class="mt-1 text-lais-body-sm font-semibold text-lais-text">
            {{ scoreLabel || statusLabel }}
          </p>
        </div>
        <span
          v-if="statusLabel"
          class="shrink-0 rounded-full bg-lais-bg-muted px-2 py-1 text-lais-label-sm font-semibold text-lais-text-muted"
        >
          {{ statusLabel }}
        </span>
      </div>

      <dl v-if="geoRows.length" class="mt-3 grid gap-2">
        <div
          v-for="row in geoRows"
          :key="row.key"
          class="flex items-start justify-between gap-3 text-lais-body-sm"
        >
          <dt class="font-medium text-lais-text-muted">{{ row.label }}</dt>
          <dd class="text-right font-semibold text-lais-text">
            {{ row.value }}
          </dd>
        </div>
      </dl>

      <ul
        v-if="reasonLabels.length"
        class="mt-3 flex flex-wrap gap-2"
        :aria-label="t('LEADS.VISITOR_INFO.MATCH_BREAKDOWN.REASONS_LABEL')"
      >
        <li
          v-for="reason in reasonLabels"
          :key="reason"
          class="rounded-full bg-white px-2 py-1 text-lais-label-sm font-semibold text-lais-text-muted"
        >
          {{ reason }}
        </li>
      </ul>
    </section>
  </div>
</template>
